import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../utils/api';

const PhotoGalleryPage = () => {
  const { sites = [] } = useOutletContext();

  // Data state
  const [photos, setPhotos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalPhotos, setTotalPhotos] = useState(0);

  // Filter state - Single month selection
  const [selectedSites, setSelectedSites] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Modal state
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showPhotoModal, setShowPhotoModal] = useState(false);

  // View mode
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  // Selection state
  const [selectedPhotos, setSelectedPhotos] = useState([]);

  // Month names for display
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Initialize with all sites selected
  useEffect(() => {
    if (sites.length > 0 && selectedSites.length === 0) {
      setSelectedSites(sites.map(s => s.id));
    }
  }, [sites]);

  // Fetch photos when filters change
  useEffect(() => {
    fetchPhotos();
  }, [selectedSites, selectedMonth, selectedYear]);

  const fetchPhotos = async () => {
    setIsLoading(true);
    setSelectedPhotos([]); // Clear selection when fetching new photos
    try {
      // Calculate start and end dates for the selected month
      const startDate = new Date(selectedYear, selectedMonth, 1).toISOString().split('T')[0];
      const endDate = new Date(selectedYear, selectedMonth + 1, 0).toISOString().split('T')[0];
      
      const params = {
        start_date: startDate,
        end_date: endDate
      };
      
      // Only add site filter if not all sites are selected
      if (selectedSites.length > 0 && selectedSites.length < sites.length) {
        params.site_ids = selectedSites.join(',');
      }

      const response = await axios.get(`${API}/admin/work-diary-photos`, {
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      // Filter by selected sites if needed
      let filteredPhotos = response.data || [];
      if (selectedSites.length > 0 && selectedSites.length < sites.length) {
        filteredPhotos = filteredPhotos.filter(photo => selectedSites.includes(photo.site_id));
      }

      setPhotos(filteredPhotos);
      setTotalPhotos(filteredPhotos.length);
    } catch (error) {
      console.error('Error fetching photos:', error);
      setPhotos([]);
      setTotalPhotos(0);
    }
    setIsLoading(false);
  };

  // Navigate to previous month
  const goToPreviousMonth = () => {
    if (selectedMonth === 0) {
      setSelectedMonth(11);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  // Navigate to next month
  const goToNextMonth = () => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    
    // Don't allow going past current month
    if (selectedYear === currentYear && selectedMonth >= currentMonth) {
      return;
    }
    
    if (selectedMonth === 11) {
      setSelectedMonth(0);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  // Check if currently viewing the current month
  const isCurrentMonth = () => {
    const today = new Date();
    return selectedYear === today.getFullYear() && selectedMonth === today.getMonth();
  };

  // Go to current month
  const goToCurrentMonth = () => {
    const today = new Date();
    setSelectedMonth(today.getMonth());
    setSelectedYear(today.getFullYear());
  };

  // Handle site toggle
  const handleSiteToggle = (siteId) => {
    setSelectedSites(prev => 
      prev.includes(siteId)
        ? prev.filter(id => id !== siteId)
        : [...prev, siteId]
    );
  };

  // Handle select all sites
  const handleSelectAllSites = () => {
    if (selectedSites.length === sites.length) {
      setSelectedSites([]);
    } else {
      setSelectedSites(sites.map(s => s.id));
    }
  };

  // Open photo in modal
  const openPhotoModal = (photo) => {
    setSelectedPhoto(photo);
    setShowPhotoModal(true);
  };

  // Close modal
  const closePhotoModal = () => {
    setShowPhotoModal(false);
    setSelectedPhoto(null);
  };

  // Navigate to previous photo
  const navigateToPreviousPhoto = () => {
    if (!selectedPhoto) return;
    
    const currentIndex = photos.findIndex(p => p.id === selectedPhoto.id);
    if (currentIndex > 0) {
      setSelectedPhoto(photos[currentIndex - 1]);
    }
  };

  // Navigate to next photo
  const navigateToNextPhoto = () => {
    if (!selectedPhoto) return;
    
    const currentIndex = photos.findIndex(p => p.id === selectedPhoto.id);
    if (currentIndex < photos.length - 1) {
      setSelectedPhoto(photos[currentIndex + 1]);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!showPhotoModal) return;
      
      if (e.key === 'ArrowLeft') {
        navigateToPreviousPhoto();
      } else if (e.key === 'ArrowRight') {
        navigateToNextPhoto();
      } else if (e.key === 'Escape') {
        closePhotoModal();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [showPhotoModal, selectedPhoto, photos]);

  // Download photo
  const downloadPhoto = (photo) => {
    const link = document.createElement('a');
    link.href = photo.gcs_url;
    link.download = photo.filename || 'photo.jpg';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Download selected photos
  const downloadSelectedPhotos = () => {
    if (selectedPhotos.length === 0) return;
    
    selectedPhotos.forEach((photoId, index) => {
      const photo = photos.find(p => p.id === photoId);
      if (photo) {
        setTimeout(() => {
          downloadPhoto(photo);
        }, index * 500); // Delay to prevent browser blocking
      }
    });
  };

  // Toggle photo selection
  const togglePhotoSelection = (photoId) => {
    setSelectedPhotos(prev => {
      if (prev.includes(photoId)) {
        return prev.filter(id => id !== photoId);
      } else {
        return [...prev, photoId];
      }
    });
  };

  // Select all photos
  const selectAllPhotos = () => {
    if (selectedPhotos.length === photos.length) {
      // Deselect all
      setSelectedPhotos([]);
    } else {
      // Select all
      setSelectedPhotos(photos.map(p => p.id));
    }
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedPhotos([]);
  };

  // Format date
  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // Reset filters
  const resetFilters = () => {
    setSelectedSites(sites.map(s => s.id));
    setSelectedPhotos([]); // Clear selection when resetting
    const today = new Date();
    setSelectedMonth(today.getMonth());
    setSelectedYear(today.getFullYear());
  };

  // Group photos by date
  const photosByDate = photos.reduce((groups, photo) => {
    const date = photo.upload_date || photo.upload_timestamp?.split('T')[0] || 'Unknown';
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(photo);
    return groups;
  }, {});

  const sortedDates = Object.keys(photosByDate).sort((a, b) => b.localeCompare(a));

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-900 -m-8 p-8 flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
        <span className="ml-3 text-gray-400 text-lg">Loading photos...</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 -m-8 p-8 space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 rounded-xl p-6 text-white">
        <h1 className="text-2xl sm:text-3xl font-bold mb-2">Photo Gallery</h1>
        <p className="text-purple-200">
          View and download work diary photos ({totalPhotos} photo{totalPhotos !== 1 ? 's' : ''})
        </p>
      </div>

      {/* Filters - Dark Theme */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-white">Filters</h3>
            <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded-full text-xs font-medium border border-purple-500/30">
              {totalPhotos} photo{totalPhotos !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={resetFilters}
              className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-gray-300 rounded-lg font-medium transition-colors flex items-center space-x-1.5 text-xs border border-slate-600"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Reset</span>
            </button>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' : 'bg-slate-700 text-gray-400 hover:bg-slate-600 border border-slate-600'}`}
                title="Grid view"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' : 'bg-slate-700 text-gray-400 hover:bg-slate-600 border border-slate-600'}`}
                title="List view"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Site Filter and Month Selection - Same Row */}
        <div className="flex gap-4">
          {/* Site Filter - Pill Selector (75% width) */}
          <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600 flex-grow" style={{flexBasis: '75%'}}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-bold text-gray-300 flex items-center">
                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                Sites ({selectedSites.length} selected)
              </h4>
              <button
                onClick={handleSelectAllSites}
                className="text-xs text-purple-400 hover:text-purple-300 font-medium transition-colors"
              >
                {selectedSites.length === sites.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {sites.map(site => (
                <button
                  key={site.id}
                  onClick={() => handleSiteToggle(site.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                    selectedSites.includes(site.id)
                      ? 'bg-purple-600 text-white shadow-lg'
                      : 'bg-slate-800 text-gray-400 hover:text-white hover:bg-slate-700 border border-slate-600'
                  }`}
                >
                  {site.site_name}
                </button>
              ))}
            </div>
          </div>

          {/* Month Selection with Navigation (25% width) */}
          <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600 flex-shrink-0" style={{flexBasis: '25%'}}>
            <h4 className="text-xs font-bold text-gray-300 mb-2 flex items-center">
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
              </svg>
              Month
            </h4>
            <div className="flex items-center space-x-2 mb-2">
              {/* Previous Month Button */}
              <button
                onClick={goToPreviousMonth}
                className="p-1.5 bg-slate-800 border border-slate-600 rounded-lg hover:bg-slate-700 transition-colors"
              >
                <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              {/* Month Display */}
              <div className="flex-1 px-2 py-1.5 bg-slate-800 border border-slate-600 rounded-lg text-center">
                <span className="text-sm font-medium text-white">
                  {monthNames[selectedMonth]} {selectedYear}
                </span>
              </div>
              
              {/* Next Month Button */}
              <button
                onClick={goToNextMonth}
                disabled={isCurrentMonth()}
                className={`p-1.5 border rounded-lg transition-colors ${
                  isCurrentMonth()
                    ? 'bg-slate-800 border-slate-700 text-gray-600 cursor-not-allowed'
                    : 'bg-slate-800 border-slate-600 hover:bg-slate-700 text-gray-300'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
            
            {/* Current Month Button */}
            <button
              onClick={goToCurrentMonth}
              disabled={isCurrentMonth()}
              className={`w-full px-3 py-1.5 rounded-lg font-medium transition-all duration-200 text-xs flex items-center justify-center ${
                isCurrentMonth()
                  ? 'bg-slate-700 text-gray-500 cursor-not-allowed'
                  : 'bg-purple-600 hover:bg-purple-500 text-white'
              }`}
            >
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
              </svg>
              Current Month
            </button>
          </div>
        </div>
      </div>

      {/* Selection Controls - Dark Theme */}
      {photos.length > 0 && (
        <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={selectAllPhotos}
                className="text-sm font-semibold text-purple-400 hover:text-purple-300 transition-colors flex items-center space-x-2"
              >
                <input
                  type="checkbox"
                  checked={selectedPhotos.length === photos.length}
                  onChange={selectAllPhotos}
                  ref={input => {
                    if (input) {
                      input.indeterminate = selectedPhotos.length > 0 && selectedPhotos.length < photos.length;
                    }
                  }}
                  className="w-4 h-4 text-purple-600 bg-slate-700 border-slate-500 rounded focus:ring-purple-500 focus:ring-2"
                />
                <span>
                  {selectedPhotos.length === photos.length ? 'Deselect All' : 'Select All'}
                </span>
              </button>
              {selectedPhotos.length > 0 && (
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-300 font-medium">
                    {selectedPhotos.length} photo{selectedPhotos.length !== 1 ? 's' : ''} selected
                  </span>
                  <button
                    onClick={clearSelection}
                    className="text-sm text-gray-400 hover:text-gray-300 underline"
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={downloadSelectedPhotos}
              disabled={selectedPhotos.length === 0}
              className={`px-6 py-2 rounded-lg font-semibold transition-all duration-200 flex items-center space-x-2 ${
                selectedPhotos.length > 0
                  ? 'bg-green-600 hover:bg-green-500 text-white shadow-lg'
                  : 'bg-slate-700 text-gray-500 cursor-not-allowed'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span>Download Selected ({selectedPhotos.length})</span>
            </button>
          </div>
        </div>
      )}

      {/* Photo Gallery - Dark Theme */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        {photos.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <p className="text-lg font-medium text-white">No photos found</p>
            <p className="text-sm text-gray-400 mt-2">
              {selectedSites.length < sites.length
                ? 'Try selecting more sites or a different month'
                : 'Photos uploaded by users will appear here'}
            </p>
          </div>
        ) : viewMode === 'grid' ? (
          /* Grid View - Grouped by Day */
          <div className="p-6">
            {sortedDates.map((date) => (
              <div key={date} className="mb-8">
                <h3 className="text-lg font-semibold text-white mb-4 pb-2 border-b border-slate-700">
                  {new Date(date).toLocaleDateString('en-GB', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                  <span className="ml-2 text-sm text-gray-400">({photosByDate[date].length} photo{photosByDate[date].length !== 1 ? 's' : ''})</span>
                </h3>
                {/* Grid with 8 photos per row */}
                <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
                  {photosByDate[date].map((photo) => (
                    <div
                      key={photo.id}
                      className={`group relative bg-slate-700 rounded-lg overflow-hidden cursor-pointer hover:shadow-lg transition-all duration-200 border-2 ${
                        selectedPhotos.includes(photo.id) ? 'border-purple-500 ring-2 ring-purple-500/30' : 'border-slate-600'
                      }`}
                      onClick={() => openPhotoModal(photo)}
                    >
                      {/* Selection Checkbox */}
                      <div className="absolute top-1.5 left-1.5 z-10" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedPhotos.includes(photo.id)}
                          onChange={() => togglePhotoSelection(photo.id)}
                          className="w-4 h-4 text-purple-600 bg-slate-700 border-slate-500 rounded focus:ring-purple-500 focus:ring-2 shadow-lg"
                        />
                      </div>
                      
                      {/* Reduced aspect ratio for smaller thumbnails */}
                      <div className="aspect-square overflow-hidden bg-slate-600">
                        <img
                          src={photo.gcs_url}
                          alt={photo.description || photo.filename}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-1.5 bg-slate-800">
                        <p className="text-xs font-medium text-white truncate">{photo.site_name}</p>
                        <p className="text-xs text-gray-400 truncate">{photo.uploaded_by || 'Unknown'} &middot; {formatDate(photo.upload_timestamp)}</p>
                      </div>
                      {/* Download button on hover */}
                      <div className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadPhoto(photo);
                          }}
                          className="bg-slate-800/90 hover:bg-slate-700 p-1.5 rounded-full shadow-lg backdrop-blur-sm border border-slate-600"
                          title="Download"
                        >
                          <svg className="w-3 h-3 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* List View - Grouped by Day */
          <div className="p-6">
            {sortedDates.map((date) => (
              <div key={date} className="mb-8">
                <h3 className="text-lg font-semibold text-white mb-4 pb-2 border-b border-slate-700">
                  {new Date(date).toLocaleDateString('en-GB', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                  <span className="ml-2 text-sm text-gray-400">({photosByDate[date].length} photo{photosByDate[date].length !== 1 ? 's' : ''})</span>
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-700">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider w-12">
                          <input
                            type="checkbox"
                            checked={photosByDate[date].every(p => selectedPhotos.includes(p.id))}
                            onChange={() => {
                              const datePhotoIds = photosByDate[date].map(p => p.id);
                              const allSelected = datePhotoIds.every(id => selectedPhotos.includes(id));
                              if (allSelected) {
                                setSelectedPhotos(prev => prev.filter(id => !datePhotoIds.includes(id)));
                              } else {
                                setSelectedPhotos(prev => [...new Set([...prev, ...datePhotoIds])]);
                              }
                            }}
                            className="w-4 h-4 text-purple-600 bg-slate-700 border-slate-500 rounded focus:ring-purple-500 focus:ring-2"
                          />
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                          Photo
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                          Site
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                          Uploaded By
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                          Time
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {photosByDate[date].map((photo, index) => (
                        <tr
                          key={photo.id}
                          className={`cursor-pointer transition-all hover:bg-slate-700/50 ${
                            selectedPhotos.includes(photo.id)
                              ? 'bg-purple-900/20'
                              : ''
                          } ${index !== photosByDate[date].length - 1 ? 'border-b border-slate-700/50' : ''}`}
                          onClick={() => openPhotoModal(photo)}
                        >
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedPhotos.includes(photo.id)}
                              onChange={() => togglePhotoSelection(photo.id)}
                              className="w-4 h-4 text-purple-600 bg-slate-700 border-slate-500 rounded focus:ring-purple-500 focus:ring-2"
                            />
                          </td>
                          <td className="px-4 py-3">
                            {/* Original thumbnail size */}
                            <img
                              src={photo.gcs_url}
                              alt={photo.filename}
                              className="w-16 h-16 object-cover rounded-lg border border-slate-600"
                            />
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-white">
                            {photo.site_name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-300">
                            {photo.uploaded_by || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-300">
                            {photo.description || <span className="italic text-gray-500">No description</span>}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-300">
                            {new Date(photo.upload_timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-center space-x-2">
                              <button
                                onClick={() => openPhotoModal(photo)}
                                className="p-2 rounded-lg bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 hover:text-purple-300 transition-all duration-200 border border-purple-500/30"
                                title="View"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => downloadPhoto(photo)}
                                className="p-2 rounded-lg bg-green-500/20 hover:bg-green-500/30 text-green-400 hover:text-green-300 transition-all duration-200 border border-green-500/30"
                                title="Download"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Photo Detail Modal - Dark Theme */}
      {showPhotoModal && selectedPhoto && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={closePhotoModal}>
          <div className="relative max-w-6xl max-h-[95vh] w-full" onClick={(e) => e.stopPropagation()}>
            {/* Close Button */}
            <button
              onClick={closePhotoModal}
              className="absolute -top-12 right-0 text-white hover:bg-white/10 rounded-lg p-2 transition-all duration-200 z-10"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Photo Counter */}
            <div className="absolute -top-12 left-0 text-white text-sm font-medium">
              {photos.findIndex(p => p.id === selectedPhoto.id) + 1} / {photos.length}
            </div>

            {/* Previous Button */}
            {photos.findIndex(p => p.id === selectedPhoto.id) > 0 && (
              <button
                onClick={navigateToPreviousPhoto}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-slate-800 hover:bg-slate-700 text-white p-3 rounded-full transition-all duration-200 shadow-xl z-10 border border-slate-600"
                title="Previous (← arrow key)"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}

            {/* Next Button */}
            {photos.findIndex(p => p.id === selectedPhoto.id) < photos.length - 1 && (
              <button
                onClick={navigateToNextPhoto}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-slate-800 hover:bg-slate-700 text-white p-3 rounded-full transition-all duration-200 shadow-xl z-10 border border-slate-600"
                title="Next (→ arrow key)"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}

            {/* Photo */}
            <div className="bg-slate-900 rounded-xl overflow-hidden shadow-2xl border border-slate-700">
              <img
                src={selectedPhoto.gcs_url}
                alt={selectedPhoto.description || selectedPhoto.filename}
                className="w-full h-auto max-h-[70vh] object-contain bg-slate-800"
              />
              
              {/* Photo Info */}
              <div className="p-6 bg-slate-800 border-t border-slate-700">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-400">Site</p>
                    <p className="text-sm font-semibold text-white">{selectedPhoto.site_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Upload Date</p>
                    <p className="text-sm font-semibold text-white">{formatDate(selectedPhoto.upload_timestamp)}</p>
                  </div>
                  {selectedPhoto.uploaded_by && (
                    <div>
                      <p className="text-xs text-gray-400">Uploaded By</p>
                      <p className="text-sm font-semibold text-white">{selectedPhoto.uploaded_by}</p>
                    </div>
                  )}
                  {selectedPhoto.description && (
                    <div className="col-span-2">
                      <p className="text-xs text-gray-400">Description</p>
                      <p className="text-sm text-gray-300">{selectedPhoto.description}</p>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t border-slate-700">
                  <button
                    onClick={() => downloadPhoto(selectedPhoto)}
                    className="bg-green-600 hover:bg-green-500 text-white px-6 py-2 rounded-lg transition-all duration-200 flex items-center space-x-2 shadow-lg"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    <span>Download</span>
                  </button>
                  <a
                    href={selectedPhoto.gcs_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-purple-600 hover:bg-purple-500 text-white px-6 py-2 rounded-lg transition-all duration-200 flex items-center space-x-2 shadow-lg"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    <span>Open in New Tab</span>
                  </a>
                  <button
                    onClick={closePhotoModal}
                    className="bg-slate-600 hover:bg-slate-500 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoGalleryPage;
